import React, { Component, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import useEmblaCarousel from 'embla-carousel-react';

import Image from '../atoms/Image';
import ArrowButton from '../atoms/ArrowButton';
import Hstack3CellExtremity from '../atoms/Hstack3CellExtremity';
import Caption from '../atoms/Caption';
import Title from '../atoms/Title';

import * as InternalPropTypes from '../../../constants/internal-types';
import * as Breakpoints from '../../../constants/breakpoints';
import TitleTypes from '../../../constants/title-types';
import ScreenSize from '../../../constants/screenSize';
import { SizesType } from '../../../types/ts/imgixQuery';
import {
  BackgroundColorEnum,
  TypeSEOWeightTypes,
} from '../../../constants/cms-constants';
import SEOWeights from '../../../constants/cms/control-options/seo-weights';
import { getSEOWeight } from '../../../utils/SEOWeight';

const styles = css({
  '& .caption': {
    opacity: '0.4',
  },
  '& .hstack-3-cell-extremety': {
    [Breakpoints.Mobile.mq]: {
      marginBottom: '48px',
    },
    [Breakpoints.TabletUp.mq]: {
      marginBottom: '64px',
    },
  },
  '& .image': {
    objectFit: 'cover',
  },

  '& .buttons': {
    display: 'flex',
    height: '100%',
    alignItems: 'flex-end',
    '& button': {
      marginLeft: '8px',
    },
  },

  '.embla__viewport': {
    overflow: 'hidden',
    [Breakpoints.Mobile.mq]: {
      marginBottom: '56px',
    },
    [Breakpoints.Tablet.mq]: {
      marginBottom: '64px',
    },
    [Breakpoints.DesktopUp.mq]: {
      marginBottom: '72px',
    },
  },
  '.embla__container': {
    backfaceVisibility: 'hidden',
    display: 'flex',
    alignItems: 'center',
    touchAction: 'pan-y',
  },
  '.embla__slide': {
    flex: '0 0 auto',
    objectFit: 'scale-down',
    paddingRight: '10px',
    paddingLeft: '10px',
    [Breakpoints.Mobile.mq]: {
      height: '130px',
      width: '180px',
    },
    [Breakpoints.TabletUp.mq]: {
      height: '320px',
      width: '450px',
    },
  },

  '& .scroll': {
    width: '100%',
    display: 'flex',
    height: '2px',
    backgroundColor: BackgroundColorEnum.LIGHTGRAY,
    '& .active': {
      transition: 'margin 400ms',
      height: '2px',
      backgroundColor: BackgroundColorEnum.BLACK,
    },
  },
});

type ImageCarouselProps = {
  images?: InternalPropTypes.Image[];
  lightHeaderText?: InternalPropTypes.RichText;
  headerText?: InternalPropTypes.RichText;
  seoWeight?: TypeSEOWeightTypes;
};

type EmblaCarouselProps = {
  lightHeaderText: InternalPropTypes.RichText;
  headerText: InternalPropTypes.RichText;
  images: InternalPropTypes.Image[];
  sizes: SizesType;
  scrollWidth: number;
  seoWeight: TypeSEOWeightTypes;
};

function EmblaCarousel(props: EmblaCarouselProps) {
  // eslint-disable-next-line react/prop-types
  const { lightHeaderText, headerText, images, sizes, scrollWidth, seoWeight } =
    props;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
  });
  const [scrollProgress, setScrollProgress] = useState(0);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onScroll = useCallback(
    (emblaApi) => {
      const progress = scrollWidth * emblaApi.selectedScrollSnap();
      setScrollProgress(progress);
    },
    [scrollWidth],
  );

  useEffect(() => {
    if (!emblaApi) return;

    onScroll(emblaApi);
    emblaApi.on('reInit', onScroll);
    emblaApi.on('scroll', onScroll);
  }, [emblaApi, onScroll]);

  const addSEOWeight = getSEOWeight(seoWeight, seoWeight);
  return (
    <div>
      <Hstack3CellExtremity
        onRenderLeftCell={() => (
          <div>
            <Caption type={Caption.types.SECTION}>{lightHeaderText}</Caption>
            <Title type={TitleTypes.SECTION} seoWeight={addSEOWeight}>
              {headerText}
            </Title>
          </div>
        )}
        onRenderRightCell={() => (
          <div className="buttons">
            <ArrowButton onClick={onPrevButtonClick} direction="left" />
            <ArrowButton onClick={onNextButtonClick} />
          </div>
        )}
      />
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {
            // eslint-disable-next-line react/prop-types
            images.map((image) => (
              <div className="embla__slide" key={image.contentfulEntryId}>
                <Image image={image} sizes={sizes} />
              </div>
            ))
          }
        </div>
      </div>
      <div className="scroll">
        <div
          className="active"
          style={{
            width: `${scrollWidth}%`,
            marginLeft: `${scrollProgress}%`,
          }}
        />
      </div>
    </div>
  );
}

class ImageCarousel extends Component<ImageCarouselProps> {
  static defaultProps = {
    images: [],
    lightHeaderText: null,
    headerText: null,
    seoWeight: SEOWeights.H2,
  };

  render() {
    const { lightHeaderText, headerText, images, seoWeight } = this.props;
    const scrollWidth = 100 / images.length;
    const sizes: SizesType = {
      [ScreenSize.Mobile]: { 'max-w': '708', 'max-h': '708' },
      [ScreenSize.Tablet]: { 'max-w': '855', 'max-h': '855' },
      [ScreenSize.Desktop]: { 'max-w': '1000', 'max-h': '500' },
    };

    return (
      <div css={styles}>
        <EmblaCarousel
          lightHeaderText={lightHeaderText}
          headerText={headerText}
          images={images}
          sizes={sizes}
          scrollWidth={scrollWidth}
          seoWeight={seoWeight}
        />
      </div>
    );
  }
}

export default ImageCarousel;
